import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import logger from "@insite/client-framework/Logger";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    billTo: BillToModel;
    extendedStyles?: OrderConfirmationBillingInformationStyles;
}

export interface OrderConfirmationBillingInformationStyles {
    billingInformationGridContainer?: GridContainerProps;
    billingInformationTitleGridItem?: GridItemProps;
    billingInformationTitle?: TypographyProps;
    billingInformationBillingAddressGridItem?: GridItemProps;
    billingAddressTitle: TypographyProps;
    billingAddress?: AddressInfoDisplayStyles;
    billingInformationCreditCardAddressGridItem?: GridItemProps;
    billingCreditCardAddressTitle?: TypographyProps;
    creditCardBillingAddress?: AddressInfoDisplayStyles;
    billingTypes?: InjectableCss;
}

export const orderConfirmationBillingInformationStyles: OrderConfirmationBillingInformationStyles = {
    billingInformationGridContainer: {
        gap: 10,
        css: css`
            padding-top: .31250625rem;
        `,
    },
    billingInformationTitleGridItem: {
        width: 12,
    },
    billingInformationTitle: {
        variant: "h5",
        as: "h2",
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.375rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1.4375rem;
            padding-top: 0;
            margin-bottom: .625rem;
        `,
    },
    billingInformationBillingAddressGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
            color: #333F48;
            font-family: Lato;
            font-size: 1rem;
            letter-spacing: 0;
            line-height: 1.5rem;
        `,
    },
    billingAddressTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                display: inline-block;
            }
            margin-bottom: .31250625rem;
        `,
    },
    billingInformationCreditCardAddressGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
            color: #333F48;
            font-family: Lato;
            font-size: 1rem;
            letter-spacing: 0;
            line-height: 1.5rem;
        `,
    },
    billingCreditCardAddressTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.375rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1.4375rem;
            margin-bottom: .625rem;
        `,
    },
    billingTypes: {
        css: css`
            color: #333F48;
            font-family: Lato;
            font-size: 1rem;
            letter-spacing: .031rem;
            line-height: 1.5rem;
        `,
    }
};

type Props = OwnProps;

const OrderConfirmationBillingInformation: FC<Props> = ({ cart, extendedStyles, billTo }) => {
    const [styles] = useState(() => mergeToNew(orderConfirmationBillingInformationStyles, extendedStyles));
    return (
        <GridContainer {...styles.billingInformationGridContainer}>
            <GridItem {...styles.billingInformationBillingAddressGridItem}>
                <Typography {...styles.billingInformationTitle}>{translate("Billing Information")}</Typography>
                <AddressInfoDisplay
                    companyName={billTo.companyName}
                    address1={billTo.address1}
                    address2={billTo.address2}
                    address3={billTo.address3}
                    address4={billTo.address4}
                    city={billTo.city}
                    postalCode={billTo.postalCode}
                    state={billTo.state ? billTo.state.abbreviation : undefined}
                    country={billTo.country ? billTo.country.abbreviation : undefined}
                    extendedStyles={styles.billingAddress}
                />
            </GridItem>
            {cart.creditCardBillingAddress && (
                <GridItem
                    {...styles.billingInformationCreditCardAddressGridItem}
                    data-test-selector="creditCardBillingAddress"
                >
                    <Typography {...styles.billingCreditCardAddressTitle}>
                        {translate("Payment Method")}
                    </Typography>
                    <Typography {...styles.billingTypes}>
                        {cart?.paymentMethod?.description} {cart?.paymentMethod?.cardType}
                    </Typography>
                    <AddressInfoDisplay
                        address1={cart.creditCardBillingAddress.address1}
                        address2={cart.creditCardBillingAddress.address2}
                        city={cart.creditCardBillingAddress.city}
                        postalCode={cart.creditCardBillingAddress.postalCode}
                        state={cart.creditCardBillingAddress.stateAbbreviation}
                        country={
                            cart.creditCardBillingAddress.countryAbbreviation
                                ? cart.creditCardBillingAddress.countryAbbreviation
                                : undefined
                        }
                        extendedStyles={styles.creditCardBillingAddress}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default OrderConfirmationBillingInformation;
